import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type InquiryRow, type LicenseRow } from '../../lib/api.js';

export function CustomerDashboard() {
  const [licenses, setLicenses] = useState<LicenseRow[]>([]);
  const [inquiries, setInquiries] = useState<InquiryRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([
      api.get<{ licenses: LicenseRow[] }>('/api/customer/licenses'),
      api.get<{ inquiries: InquiryRow[] }>('/api/customer/inquiries'),
    ])
      .then(([l, i]) => {
        setLicenses(l.licenses);
        setInquiries(i.inquiries);
      })
      .catch((err) => setError((err as Error).message));
  }, []);

  const activeLicenses = licenses.filter((l) => !l.revokedAt).length;
  const openInquiries = inquiries.filter((i) => i.status === 'open').length;

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">대시보드</h1>
      </header>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card title="활성 라이선스" value={String(activeLicenses)} link="/customer/licenses" />
        <Card title="열린 문의" value={String(openInquiries)} link="/customer/inquiries" />
        <Card title="새 활성화" value="→" link="/activate" />
      </div>

      <h2 className="text-lg font-medium mb-2">최근 문의</h2>
      <div className="bg-surface border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <tbody className="divide-y divide-neutral-100">
            {inquiries.slice(0, 5).map((i) => (
              <tr key={i.id} className="hover:bg-neutral-50/50">
                <td className="px-4 py-2">
                  <Link
                    to={`/customer/inquiries/${i.id}`}
                    className="text-brand-600 hover:underline font-medium"
                  >
                    {i.subject}
                  </Link>
                </td>
                <td className="px-4 py-2 text-neutral-500 text-xs">
                  {i.category === 'product' ? '제품' : '라이선스'}
                </td>
                <td className="px-4 py-2 text-neutral-500 text-xs">
                  {new Date(i.lastMessageAt).toLocaleDateString('ko-KR')}
                </td>
              </tr>
            ))}
            {inquiries.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-neutral-500">아직 문의가 없습니다.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ title, value, link }: { title: string; value: string; link: string }) {
  return (
    <Link
      to={link}
      className="block p-5 bg-surface border border-neutral-200 rounded-lg hover:border-brand-400 transition-colors"
    >
      <div className="text-xs uppercase tracking-wide text-neutral-500">{title}</div>
      <div className="mt-1 text-3xl font-semibold">{value}</div>
    </Link>
  );
}
